import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { formatEngineSizeForDisplay } from '../../utils/engineSize';

function InventorySection({
  loading,
  message,
  inventorySearch,
  setInventorySearch,
  paginatedInventoryVehicles,
  filteredInventoryVehicles,
  inventoryPage,
  totalInventoryPages,
  pageSize,
  setInventoryPage,
  toggleSold,
  deleteSoldVehicle,
}) {
  const sectionRef = useRef(null);

  const scrollToTop = () => {
    const section = sectionRef.current;

    // Always attempt browser-level scrolling first.
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
    document.body.scrollTo({ top: 0, behavior: 'smooth' });

    // Then scroll any ancestor containers that are actually scrollable.
    if (!section) {
      return;
    }

    let current = section.parentElement;
    while (current) {
      const style = window.getComputedStyle(current);
      const canScrollY = /(auto|scroll)/.test(style.overflowY);
      if (canScrollY && current.scrollHeight > current.clientHeight) {
        current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      current = current.parentElement;
    }

    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const changeInventoryPage = (nextPage) => {
    setInventoryPage(nextPage);

    // Run after render so the newly paged content is in the DOM before scrolling.
    requestAnimationFrame(() => {
      scrollToTop();
    });
  };

  return (
    <section className="inventory-block" ref={sectionRef}>
      <div className="section-header">
        <h2>Manage Inventory</h2>
      </div>

      {loading && <p className="status-text">Loading stock...</p>}
      {message && <p className="status-text">{message}</p>}

      <div className="inventory-tools">
        <input
          type="text"
          className="inventory-search-input"
          placeholder="Search by reg, make, or model"
          value={inventorySearch}
          onChange={(event) => setInventorySearch(event.target.value)}
        />
        <p className="inventory-results-count">
          Showing {paginatedInventoryVehicles.length} of {filteredInventoryVehicles.length}
        </p>
      </div>

      <div className="card-grid">
        {paginatedInventoryVehicles.map((vehicle) => (
          <article className="vehicle-card" key={vehicle.id}>
            <div className="vehicle-image-wrap">
              <img src={vehicle.imageUrls?.[0] || vehicle.imageUrl} alt={`${vehicle.make} ${vehicle.model}`} />
            </div>
            <div className="vehicle-card-body">
              <h3>
                {vehicle.make} {vehicle.model}
              </h3>
              <p>
                {vehicle.year} | {Number(vehicle.mileage).toLocaleString()} miles
              </p>
              <p>
                {vehicle.fuelType || 'n/a'} | {vehicle.gearboxType || 'n/a'} | {formatEngineSizeForDisplay(vehicle.engineSize)}
              </p>
              <p>Reg: {vehicle.registrationNumber || 'n/a'}</p>
              <div className="prices">
                <strong>£{Number(vehicle.salePrice).toLocaleString()}</strong>
                <span>or £{Number(vehicle.financePrice).toLocaleString()}/month</span>
              </div>
              <p>{vehicle.description}</p>
              <div className="vehicle-card-actions">
                <Link to={`/admin/vehicles/${vehicle.id}/edit`} className="ghost-btn edit-link-btn">
                  Edit
                </Link>
                <button
                  type="button"
                  onClick={() => toggleSold(vehicle)}
                  className={vehicle.isSold ? 'sold-btn' : 'available-btn'}
                >
                  {vehicle.isSold ? 'Available' : 'Mark As Sold'}
                </button>
                {vehicle.isSold && (
                  <button
                    type="button"
                    className="ghost-btn"
                    onClick={() => deleteSoldVehicle(vehicle)}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>

      {filteredInventoryVehicles.length === 0 && !loading && (
        <p className="status-text">No vehicles found for that search.</p>
      )}

      {filteredInventoryVehicles.length > pageSize && (
        <div className="inventory-pagination">
          <button
            type="button"
            className="ghost-btn"
            onClick={() => changeInventoryPage((current) => Math.max(1, current - 1))}
            disabled={inventoryPage === 1}
          >
            Previous
          </button>
          <span className="inventory-page-info">
            Page {inventoryPage} of {totalInventoryPages}
          </span>
          <button
            type="button"
            className="ghost-btn"
            onClick={() => changeInventoryPage((current) => Math.min(totalInventoryPages, current + 1))}
            disabled={inventoryPage === totalInventoryPages}
          >
            Next
          </button>
        </div>
      )}
    </section>
  );
}

export default InventorySection;
